import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X, type LucideIcon } from 'lucide-react';
import { Text } from '../Typography/Text';
import { Heading } from '../Typography/Heading';
import { Button } from '../Button/Button';
import './Alert.css';

export type AlertTone = 'danger' | 'warning' | 'info' | 'success';

/**
 * How the alert is announced to assistive technology:
 * - 'assertive' → role="alert" (errors/warnings that appear after a user action)
 * - 'polite'    → role="status" (passive info, success, load errors on first render)
 * - 'off'       → no live role (static notice that is part of the surrounding content,
 *                 or an alert rendered inside an existing live region — avoids double
 *                 announcements)
 */
export type AlertLive = 'assertive' | 'polite' | 'off';

export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'role'> {
  /** Meaning of the message. Defaults to 'danger'. */
  tone?: AlertTone;
  /** Short, bold first line (e.g. "Dosing plan could not be saved"). */
  title?: ReactNode;
  /**
   * Render the title as a heading of this level (e.g. a page/section-level error
   * state that belongs in the document outline). Default: plain bold text.
   */
  titleLevel?: 2 | 3 | 4 | 5 | 6;
  /** The message itself (e.g. the translated reason). */
  children?: ReactNode;
  /** Optional action below the message, e.g. a "Try again" <Button>. */
  action?: ReactNode;
  /** When set, a dismiss button is rendered; it needs `dismissLabel` as its accessible name. */
  onDismiss?: () => void;
  /** Accessible name of the dismiss button (translated, e.g. t('common.dismiss')). */
  dismissLabel?: string;
  /**
   * Announcement politeness. Defaults to 'assertive' for danger/warning and
   * 'polite' for info/success.
   */
  live?: AlertLive;
  /** Tighter padding for alerts inside cards, list rows or dialogs. */
  compact?: boolean;
  className?: string;
}

const ICONS: ReadonlyMap<AlertTone, LucideIcon> = new Map<AlertTone, LucideIcon>([
  ['danger', AlertCircle],
  ['warning', AlertTriangle],
  ['info', Info],
  ['success', CheckCircle2],
]);

const ROLES: ReadonlyMap<AlertLive, 'alert' | 'status' | undefined> = new Map<AlertLive, 'alert' | 'status' | undefined>([
  ['assertive', 'alert'],
  ['polite', 'status'],
  ['off', undefined],
]);

/**
 * Alert — the one inline message banner (form/page errors, warnings, notices).
 *
 * Meaning is never colour-only: every tone has its own icon and the message is
 * text. danger/warning are announced immediately (`role="alert"`), info/success
 * politely (`role="status"`); `live` overrides this.
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert({
  tone = 'danger',
  title,
  titleLevel,
  children,
  action,
  onDismiss,
  dismissLabel,
  live,
  compact = false,
  className = '',
  ...rest
}, ref) {
  const IconComponent = ICONS.get(tone) ?? AlertCircle;
  const politeness: AlertLive = live ?? (tone === 'danger' || tone === 'warning' ? 'assertive' : 'polite');
  const role = ROLES.get(politeness);
  const classes = ['rg-alert', `rg-alert--${tone}`, compact ? 'rg-alert--compact' : '', className]
    .filter(Boolean)
    .join(' ');
  return (
    <div ref={ref} role={role} className={classes} {...rest}>
      <IconComponent className="rg-alert__icon" size={compact ? 16 : 20} strokeWidth={2} aria-hidden="true" focusable="false" />
      <div className="rg-alert__content">
        {title !== undefined && title !== null && titleLevel === undefined && (
          <Text as="span" size="sm" weight="semibold" color="inherit" className="rg-alert__title">
            {title}
          </Text>
        )}
        {title !== undefined && title !== null && titleLevel !== undefined && (
          <Heading level={titleLevel} className="rg-alert__title rg-alert__title--heading">
            {title}
          </Heading>
        )}
        {children !== undefined && children !== null && (
          <Text as="span" size="sm" color="inherit" className="rg-alert__message">
            {children}
          </Text>
        )}
        {action !== undefined && action !== null && <div className="rg-alert__action">{action}</div>}
      </div>
      {onDismiss && (
        <Button
          variant="ghost"
          size="sm"
          className="rg-alert__dismiss"
          onClick={onDismiss}
          aria-label={dismissLabel}
        >
          <X size={16} aria-hidden="true" focusable="false" />
        </Button>
      )}
    </div>
  );
});

Alert.displayName = 'Alert';
